import type {
    ChipsField,
    CheckboxGroupField,
    Field,
    FieldValue,
    NumberField,
    RadioField,
    SelectField,
    SliderField,
    TextareaField,
} from '../../types/generator-config';

interface RenderedFieldProps {
    field: Field;
    value: FieldValue;
    invalid?: boolean;
    onChange: (next: FieldValue) => void;
}

export function RenderedField({ field, value, invalid, onChange }: RenderedFieldProps) {
    return (
        <div className={`r-field${invalid ? ' is-invalid' : ''}`}>
            <label className="r-field-label" htmlFor={`f-${field.id}`}>
                {field.label || <em>Sans titre</em>}
                {field.required && <span className="r-field-required" aria-label="requis">*</span>}
            </label>
            {field.helper && <div className="r-field-helper">{field.helper}</div>}
            <div className="r-field-control">
                <Control field={field} value={value} invalid={invalid} onChange={onChange} />
            </div>
            {invalid && (
                <div className="r-field-error" role="alert">
                    Ce champ est obligatoire.
                </div>
            )}
        </div>
    );
}

function Control({ field, value, invalid, onChange }: RenderedFieldProps) {
    switch (field.type) {
        case 'select': {
            const f = field as SelectField;
            return (
                <select
                    id={`f-${f.id}`}
                    className="r-select"
                    value={(value as string) ?? ''}
                    onChange={(e) => onChange(e.target.value || null)}
                    aria-invalid={invalid || undefined}
                >
                    <option value="">— Choisir —</option>
                    {f.options.map((o) => (
                        <option key={o.id} value={o.id}>{o.label}</option>
                    ))}
                </select>
            );
        }
        case 'chips': {
            const f = field as ChipsField;
            if (f.multi) {
                const selected = Array.isArray(value) ? (value as string[]) : [];
                const toggle = (optId: string) => {
                    if (selected.includes(optId)) {
                        onChange(selected.filter((v) => v !== optId));
                    } else {
                        onChange([...selected, optId]);
                    }
                };
                return (
                    <div className="r-chips" role="group">
                        {f.options.map((o) => {
                            const active = selected.includes(o.id);
                            return (
                                <button
                                    key={o.id}
                                    type="button"
                                    className={`r-chip${active ? ' is-active' : ''}`}
                                    onClick={() => toggle(o.id)}
                                    aria-pressed={active}
                                >
                                    {o.icon && <span className="r-chip-icon" aria-hidden>{o.icon}</span>}
                                    <span>{o.label}</span>
                                </button>
                            );
                        })}
                    </div>
                );
            }
            const single = (value as string) ?? null;
            return (
                <div className="r-chips" role="radiogroup">
                    {f.options.map((o) => {
                        const active = single === o.id;
                        return (
                            <button
                                key={o.id}
                                type="button"
                                role="radio"
                                aria-checked={active}
                                className={`r-chip${active ? ' is-active' : ''}`}
                                onClick={() => onChange(o.id)}
                            >
                                {o.icon && <span className="r-chip-icon" aria-hidden>{o.icon}</span>}
                                <span>{o.label}</span>
                            </button>
                        );
                    })}
                </div>
            );
        }
        case 'radio': {
            const f = field as RadioField;
            const selected = (value as string) ?? '';
            return (
                <div className="r-radios" role="radiogroup">
                    {f.options.map((o) => {
                        const active = selected === o.id;
                        return (
                            <label key={o.id} className={`r-radio${active ? ' is-active' : ''}`}>
                                <input
                                    type="radio"
                                    name={f.id}
                                    value={o.id}
                                    checked={active}
                                    onChange={() => onChange(o.id)}
                                />
                                <span className="r-radio-dot" aria-hidden />
                                <span>{o.label}</span>
                            </label>
                        );
                    })}
                </div>
            );
        }
        case 'checkbox-group': {
            const f = field as CheckboxGroupField;
            const selected = Array.isArray(value) ? (value as string[]) : [];
            const toggle = (optId: string) => {
                if (selected.includes(optId)) {
                    onChange(selected.filter((v) => v !== optId));
                } else {
                    onChange([...selected, optId]);
                }
            };
            return (
                <div className="r-checks">
                    {f.options.map((o) => {
                        const active = selected.includes(o.id);
                        return (
                            <label key={o.id} className={`r-check${active ? ' is-active' : ''}`}>
                                <input
                                    type="checkbox"
                                    checked={active}
                                    onChange={() => toggle(o.id)}
                                />
                                <span className="r-check-box" aria-hidden />
                                <span>{o.label}</span>
                            </label>
                        );
                    })}
                </div>
            );
        }
        case 'slider': {
            const f = field as SliderField;
            const n = typeof value === 'number' ? value : Math.round((f.min + f.max) / 2);
            return (
                <div className="r-slider">
                    <input
                        id={`f-${f.id}`}
                        type="range"
                        min={f.min}
                        max={f.max}
                        step={f.step ?? 1}
                        value={n}
                        onChange={(e) => onChange(Number(e.target.value))}
                    />
                    <div className="r-slider-value">
                        {n}{f.unit ? ` ${f.unit}` : ''}
                    </div>
                </div>
            );
        }
        case 'number': {
            const f = field as NumberField;
            const raw = value === null || value === undefined ? '' : String(value);
            return (
                <div className="r-number">
                    <input
                        id={`f-${f.id}`}
                        type="number"
                        value={raw}
                        placeholder="0"
                        min={f.min}
                        max={f.max}
                        step={f.step ?? 1}
                        onChange={(e) => {
                            const v = e.target.value;
                            onChange(v === '' ? null : Number(v));
                        }}
                        aria-invalid={invalid || undefined}
                    />
                    {f.unit && <span className="r-number-unit">{f.unit}</span>}
                </div>
            );
        }
        case 'textarea': {
            const f = field as TextareaField;
            return (
                <textarea
                    id={`f-${f.id}`}
                    className="r-textarea"
                    value={(value as string) ?? ''}
                    placeholder={f.placeholder}
                    maxLength={f.maxLength}
                    rows={3}
                    onChange={(e) => onChange(e.target.value)}
                    aria-invalid={invalid || undefined}
                />
            );
        }
    }
}
